// Small display pieces shared by the Update Batches list and detail pages.
import type { BatchProgress, BatchStatus } from '@/api/update-batches';

export type StageKey = 'apk_built' | 'qa_checked' | 'aab_built' | 'published';

export const STAGES: { key: StageKey; label: string }[] = [
  { key: 'apk_built', label: 'APK Built' },
  { key: 'qa_checked', label: 'QA Checked' },
  { key: 'aab_built', label: 'AAB Built' },
  { key: 'published', label: 'Published' },
];

/** One stage of the pipeline as the bar and the caption show it. QA counts
 *  every verdict, passed or failed, so the caption can spell out both. */
export interface StageValue {
  value: number;
  total: number;
  /** QA only: how many of `value` failed; drawn in red at the end of the bar. */
  failed?: number;
  /** Published only: sent to Play review, not live yet — not part of `value`;
   *  drawn in amber after the live part. */
  review?: number;
}

export function stageValue(p: BatchProgress, key: StageKey): StageValue {
  if (key === 'qa_checked') return { value: p.qa_approved + p.qa_rejected, total: p.total, failed: p.qa_rejected };
  if (key === 'published') return { value: p.published, total: p.total, review: p.in_review ?? 0 };
  return { value: p[key], total: p.total };
}

export function pct(n: number, total: number): number {
  return total > 0 ? Math.round((n / total) * 100) : 0;
}

/** Fully done and nothing failed: the bar and the caption go green. */
export function stageComplete(s: StageValue): boolean {
  return s.total > 0 && s.value === s.total && !(s.failed ?? 0);
}

export function StageBar({ value, total, failed = 0, review = 0, thin = false }: StageValue & { thin?: boolean }) {
  const ok = Math.max(0, value - failed);
  const color = stageComplete({ value, total, failed }) ? 'bg-success' : 'bg-primary';
  return (
    <div className={`w-full rounded-full bg-surface-muted overflow-hidden flex ${thin ? 'h-1' : 'h-1.5'}`}>
      <div className={`h-full ${color}`} style={{ width: `${pct(ok, total)}%` }} />
      {review > 0 && <div className="h-full bg-warning" style={{ width: `${pct(review, total)}%` }} />}
      {failed > 0 && <div className="h-full bg-danger" style={{ width: `${pct(failed, total)}%` }} />}
    </div>
  );
}

/** "1/2" for a plain stage; "✓ 1 passed  ✕ 1 failed" for QA, with the
 *  failed part left out when nothing failed; "1/3  ⟳ 2 in review" for
 *  Published, with the review part left out when nothing is in review. */
export function StageCaption({ stage, className = '' }: { stage: StageValue; className?: string }) {
  if (stage.review !== undefined) {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`}>
        <span className={stageComplete(stage) ? 'text-success' : 'text-content-muted'}>{stage.value}/{stage.total}</span>
        {stage.review > 0 && <span className="text-warning">⟳ {stage.review} in review</span>}
      </span>
    );
  }
  if (stage.failed === undefined) {
    return <span className={`${stageComplete(stage) ? 'text-success' : 'text-content-muted'} ${className}`}>{stage.value}/{stage.total}</span>;
  }
  const passed = stage.value - stage.failed;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span className={passed > 0 ? 'text-success' : 'text-content-muted'}>✓ {passed} passed</span>
      {stage.failed > 0 && <span className="text-danger">✕ {stage.failed} failed</span>}
    </span>
  );
}

/** Colour key for the stage bars. */
export function StageLegend() {
  return (
    <div className="flex items-center gap-4 text-xs text-content-subtle">
      <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-success" /> QA Passed</span>
      <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-danger" /> QA Failed</span>
      <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-warning" /> In review</span>
      <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-primary" /> In progress</span>
      <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-surface-muted border border-border" /> Pending</span>
    </div>
  );
}

export function BatchStatusBadge({ status }: { status: BatchStatus }) {
  const cls = status === 'completed' ? 'bg-success-soft text-success'
    : status === 'archived' ? 'bg-surface-muted text-content-subtle'
    : 'bg-primary/10 text-primary';
  const text = status === 'completed' ? 'Completed' : status === 'archived' ? 'Archived' : 'Active';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{text}</span>;
}

export function fmtUnix(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function repoLabel(repos: { id: number; name: string }[]): string {
  if (repos.length === 0) return '—';
  if (repos.length === 1) return repos[0]!.name;
  return `${repos.length} repos`;
}
